import { isBefore, parseISO } from 'date-fns';

import type { OpeningScheduleResponse } from 'generated/index';
import { toDateString } from 'utils/date';

/** Valid from {@code validFrom} to {@code validTo}, the latter exclusive; no end is an open end. */
export const scheduleCoversDate = (
  schedule: OpeningScheduleResponse,
  date: Date
): boolean => {
  const day = parseISO(toDateString(date));
  if (isBefore(day, parseISO(schedule.validFrom))) return false;
  return !schedule.validTo || isBefore(day, parseISO(schedule.validTo));
};

export const findScheduleCoveringDate = (
  schedules: OpeningScheduleResponse[],
  date: Date | null
): OpeningScheduleResponse | undefined =>
  date
    ? schedules.find((schedule) => scheduleCoversDate(schedule, date))
    : undefined;

/**
 * The schedule that follows on from this one, from the day it is valid to and with the same lines.
 * There is no import: without this an organisation types every location in again each school year.
 * The name is left for the administrator, since it usually names the year.
 *
 * It runs up to the next schedule of the unit, or has no end when there is none. Undefined when
 * there is nothing to extend into: an open end, or a next schedule that starts right where this one
 * ends.
 */
export const extendSchedule = (
  schedule: OpeningScheduleResponse,
  schedules: OpeningScheduleResponse[]
): OpeningScheduleResponse | undefined => {
  const { validTo } = schedule;
  if (!validTo) return undefined;
  const next = schedules
    .map((other) => other.validFrom)
    .filter((validFrom) => validFrom >= validTo)
    .sort()[0];
  if (next === validTo) return undefined;
  return {
    ...schedule,
    name: '',
    description: undefined,
    validFrom: validTo,
    validTo: next
  };
};
